/**
 * Composable pour détecter si l'application est dans une iframe
 * et obtenir l'URL du site parent
 */

import { useParentOrigin } from './useParentOrigin';

export function useIframeDetection() {
    /**
     * Vérifie si l'application est dans une iframe
     */
    function isInIframe(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            // Si on ne peut pas accéder à window.top (CORS), on est probablement dans une iframe
            return true;
        }
    }

    /**
     * Obtient l'URL du parent (si possible)
     * Retourne null si impossible d'accéder (restrictions CORS)
     */
    function getParentUrl(): string | null {
        try {
            // Essayer d'accéder à l'URL du parent directement
            if (window.parent && window.parent.location && window.parent.location.href) {
                return window.parent.location.href;
            }
        } catch (e) {
            // Bloqué par CORS
        }

        if (document.referrer) {
            return document.referrer;
        }

        return null;
    }

    /**
     * Obtient l'URL à utiliser pour l'originUrl
     * - Si dans une iframe : URL du parent (ou referrer)
     * - Sinon : URL actuelle
     */
    function getOriginUrl(): string {
        const inIframe = isInIframe();

        if (inIframe) {
            const parentUrl = getParentUrl();
            if (parentUrl) {
                return parentUrl;
            }
        }

        return window.location.href;
    }

    /**
     * Demande l'URL du parent via postMessage
     * Retourne une Promise qui se résout avec l'URL
     */
    function requestParentUrl(): Promise<string> {
        const { outboundTargetOrigin, isInboundOriginAllowed } = useParentOrigin();
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Timeout: Pas de réponse du parent'));
            }, 5000); // 5 secondes de timeout

            // Écouter la réponse
            const handleMessage = (event: MessageEvent) => {
                if (!isInboundOriginAllowed(event.origin)) {
                    return;
                }
                try {
                    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                    if (data.eventType === 'sendParentUrl' && data.url) {
                        clearTimeout(timeout);
                        window.removeEventListener('message', handleMessage);
                        resolve(data.url);
                    }
                } catch (e) {
                    // Ignore les messages mal formés
                }
            };

            window.addEventListener('message', handleMessage);

            // Demander l'URL au parent
            const message = {
                eventType: 'askParentUrl'
            };
            window.parent.postMessage(JSON.stringify(message), outboundTargetOrigin());
        });
    }

    return {
        isInIframe,
        getParentUrl,
        getOriginUrl,
        requestParentUrl
    };
}
